import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import type { MaPosition } from "@/lib/material";
import {
  maPositionOrderHint,
  maPositionOrderState,
} from "@/pages/material/maListDisplay";

export interface MaBereitgelegtDialogProps {
  open: boolean;
  maNummer: string;
  positionen: MaPosition[];
  loading?: boolean;
  onClose: () => void;
  onConfirm: (positionIds: number[]) => void;
}

function positionIsOrdered(p: MaPosition): boolean {
  const state = maPositionOrderState(p);
  return state === "bestellt" || state === "geliefert";
}

function positionIsBereitgelegt(p: MaPosition): boolean {
  return Boolean(p.bereitgelegt_at);
}

function positionIsClosed(p: MaPosition): boolean {
  const state = maPositionOrderState(p);
  return state === "storniert" || state === "abgelehnt";
}

function buildSelection(positionen: MaPosition[]): Record<number, boolean> {
  const next: Record<number, boolean> = {};
  for (const p of positionen) {
    if (p.id == null) continue;
    // Standard: offen (nicht Lager, nicht bestellt, nicht storniert/abgelehnt)
    next[p.id] =
      !positionIsBereitgelegt(p) &&
      !positionIsOrdered(p) &&
      !positionIsClosed(p);
  }
  return next;
}

export function MaBereitgelegtDialog({
  open,
  maNummer,
  positionen,
  loading = false,
  onClose,
  onConfirm,
}: MaBereitgelegtDialogProps) {
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSelected({});
      setError(null);
      return;
    }
    setSelected(buildSelection(positionen));
    setError(null);
  }, [open, positionen]);

  const selectedIds = useMemo(
    () =>
      positionen
        .filter((p) => p.id != null && selected[p.id])
        .map((p) => p.id),
    [positionen, selected],
  );
  const allSelected =
    positionen.length > 0 && positionen.every((p) => p.id != null && selected[p.id]);

  function toggleAll(checked: boolean) {
    const next: Record<number, boolean> = {};
    for (const p of positionen) {
      if (p.id == null) continue;
      next[p.id] = checked;
    }
    setSelected(next);
  }

  function handleConfirm() {
    if (!selectedIds.length) {
      setError("Bitte mindestens eine Position auswählen.");
      return;
    }
    setError(null);
    onConfirm(selectedIds);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={selectedIds.length > 0}
      size="lg"
      title="Im Lager bereitgelegt"
      description={`Materialanforderung ${maNummer} – Positionen wählen, die im Lager bereitliegen. Die übrigen können Sie bestellen.`}
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={loading} variant="ghost" />
          <Button
            variant="primary"
            loading={loading}
            disabled={loading || selectedIds.length === 0}
            onClick={handleConfirm}
          >
            {selectedIds.length === 1
              ? "1 Position bereitlegen"
              : `${selectedIds.length || ""} Positionen bereitlegen`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-label">Positionen</p>
          {positionen.length > 0 ? (
            <button
              type="button"
              className="text-xs text-accent hover:underline"
              onClick={() => toggleAll(!allSelected)}
            >
              {allSelected ? "Keine auswählen" : "Alle auswählen"}
            </button>
          ) : null}
        </div>
        <p className="text-xs text-label-secondary">
          Markieren Sie die Positionen, die im Lager bereitliegen. Bereits bestellte oder
          bereitgelegte Positionen sind gekennzeichnet und bleiben wählbar. Stornierte und
          abgelehnte Positionen können nicht bereitgelegt werden.
        </p>

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        {positionen.length === 0 ? (
          <p className="rounded-ios border border-separator bg-fill-quaternary/15 px-3 py-2 text-sm text-label-secondary">
            Keine Positionen vorhanden – die gesamte Anforderung wird bereitgelegt.
          </p>
        ) : (
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {positionen.map((p, idx) => {
              const ordered = positionIsOrdered(p);
              const bereitgelegt = positionIsBereitgelegt(p);
              const closed = positionIsClosed(p);
              const hint = maPositionOrderHint(p);
              const orderState = maPositionOrderState(p);
              const checked = !!selected[p.id];
              const menge =
                p.menge != null && Number.isFinite(Number(p.menge))
                  ? String(p.menge)
                  : "–";
              const einheit = (p.einheit || "").trim();
              return (
                <label
                  key={p.id}
                  className={`flex items-start gap-2 rounded-ios border px-3 py-2 text-sm ${
                    closed
                      ? "cursor-not-allowed border-separator bg-fill-quaternary/20 opacity-70"
                      : bereitgelegt
                        ? "cursor-pointer border-status-green/35 bg-status-green/10"
                        : ordered
                          ? "cursor-pointer border-indigo/30 bg-indigo/5"
                          : "cursor-pointer border-separator bg-card"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                    checked={checked}
                    disabled={closed}
                    onChange={(e) =>
                      setSelected((prev) => ({ ...prev, [p.id]: e.target.checked }))
                    }
                  />
                  <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">
                    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-label-secondary">#{idx + 1}</span>
                      <span className="font-medium text-label">
                        {menge}
                        {einheit ? ` ${einheit}` : ""}
                      </span>
                      <span className="min-w-0 text-label">{p.text || "–"}</span>
                    </span>
                    {closed ? (
                      <span className="mt-0.5 block text-xs text-label-secondary">
                        {orderState === "abgelehnt" ? "abgelehnt" : "storniert"}
                        {hint ? ` · ${hint}` : ""}
                      </span>
                    ) : bereitgelegt ? (
                      <span className="mt-0.5 block text-xs text-status-green">
                        bereits im Lager bereitgelegt
                      </span>
                    ) : hint ? (
                      <span
                        className={`mt-0.5 block text-xs ${
                          orderState === "bestellt" || orderState === "geliefert"
                            ? "text-indigo"
                            : "text-accent"
                        }`}
                      >
                        {orderState === "bestellt" || orderState === "geliefert"
                          ? "bereits in "
                          : ""}
                        {hint}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
}
